import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import { FaEdit, FaTrash, FaCheck, FaTimes, FaEye } from 'react-icons/fa';

function Resources() {
  const [resources, setResources] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { user } = useAuth();

  const fetchResources = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (filterStatus) params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;
      const res = await api.get('/resources/admin/all', { params });
      setResources(res.data.resources);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    setSelected(new Set());
  };

  useEffect(() => { fetchResources(); }, [filterStatus]);

  const handleApprove = async (id) => {
    await api.put(`/resources/${id}/approve`);
    fetchResources(pagination.page);
  };

  const handleReject = async (id) => {
    await api.put(`/resources/${id}/reject`);
    fetchResources(pagination.page);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    await api.delete(`/resources/${id}`);
    fetchResources(pagination.page);
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === resources.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(resources.map(r => r.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected resource(s)?`)) return;
    setBulkDeleting(true);
    try {
      await api.post('/resources/bulk-delete', { ids: Array.from(selected) });
      fetchResources(pagination.page);
    } catch (err) {
      console.error(err);
      alert('Failed to delete resources.');
    }
    setBulkDeleting(false);
  };

  const statusBadge = (status) => {
    const colors = { published: 'badge-success', pending: 'badge-warning', draft: 'badge-secondary', rejected: 'badge-danger' };
    return <span className={`badge ${colors[status] || ''}`}>{status}</span>;
  };

  const isAllSelected = resources.length > 0 && selected.size === resources.length;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h2>Resources</h2>
        <Link to="/admin/resources/new" className="btn btn-primary">+ Add Resource</Link>
      </div>

      <div className="admin-filters">
        <input
          type="text"
          placeholder="Search resources..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchResources()}
          className="form-input"
        />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-select">
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="pending">Pending</option>
          <option value="draft">Draft</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && ['admin', 'editor'].includes(user?.role) && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">{selected.size} selected</span>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
          >
            <FaTrash /> {bulkDeleting ? 'Deleting...' : `Delete ${selected.size} item(s)`}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelected(new Set())}>
            Cancel
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-text">Loading...</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                {['admin', 'editor'].includes(user?.role) && (
                  <th className="th-checkbox">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                )}
                <th>Icon</th>
                <th>Name</th>
                <th>Category</th>
                <th>Status</th>
                <th>Views</th>
                <th>Submitted By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {resources.map(r => (
                <tr key={r.id} className={selected.has(r.id) ? 'row-selected' : ''}>
                  {['admin', 'editor'].includes(user?.role) && (
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                      />
                    </td>
                  )}
                  <td>
                    <img
                      src={r.icon_url || `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=32`}
                      alt=""
                      className="table-icon"
                      onError={e => { e.target.src = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(r.link)}&sz=32`; }}
                    />
                  </td>
                  <td><strong>{r.name}</strong></td>
                  <td>{r.category_name || '-'}</td>
                  <td>{statusBadge(r.status)}</td>
                  <td>{r.views}</td>
                  <td>{r.submitted_by_name || '-'}</td>
                  <td className="table-actions">
                    {r.status === 'pending' && ['admin', 'editor'].includes(user?.role) && (
                      <>
                        <button onClick={() => handleApprove(r.id)} className="btn-icon btn-success" title="Approve"><FaCheck /></button>
                        <button onClick={() => handleReject(r.id)} className="btn-icon btn-danger" title="Reject"><FaTimes /></button>
                      </>
                    )}
                    <Link to={`/admin/resources/edit/${r.id}`} className="btn-icon btn-info" title="Edit"><FaEdit /></Link>
                    {['admin', 'editor'].includes(user?.role) && (
                      <button onClick={() => handleDelete(r.id)} className="btn-icon btn-danger" title="Delete"><FaTrash /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button disabled={pagination.page <= 1} onClick={() => fetchResources(pagination.page - 1)} className="page-btn">Previous</button>
              <span>Page {pagination.page} of {pagination.pages}</span>
              <button disabled={pagination.page >= pagination.pages} onClick={() => fetchResources(pagination.page + 1)} className="page-btn">Next</button>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

export default Resources;
